<template>
  <li v-if="groupsNotEmpty.length > 0" class="valign-wrapper">
    <div v-for="group in groupsNotEmpty" :key="group.key">
      <a
        class="waves-effect waves-block waves-light header-button notification-button dropdown-menu"
        data-closeOnClick="false"
        href="javascript:void(0);"
        :data-target="`notifications-dropdown-${group.key}`"
      >
        <i class="material-icons">
          {{ group.icon }}
          <small v-if="group.unread > 0" class="notification-badge">{{
            group.unread
          }}</small>
        </i>
      </a>

      <div
        :id="`notifications-dropdown-${group.key}`"
        class="notifications-dropdown dropdown-content"
      >
        <h5 v-if="group.notifications.length === 0">Aucune notification</h5>
        <notifications-list v-else :notifications="group.notifications" />
      </div>
    </div>
  </li>
</template>

<script>
import {
  NOTIFICATION_STATE,
  NOTIFICATION_TYPE,
} from '@/modules/notification/notification.data'
import { mapGetters } from 'vuex'
import notificationsList from './notifications.list'
import { getDeepValue, setIntervalIfVisible } from '@/plugins/utils'

const FORFAIT_JOUR_QUESTION_KEY = 'FORFAIT_JOUR'

export default {
  name: 'Notifications',
  components: { notificationsList },
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      refreshNotif: null,
      forfaitJourQuestionPending: false,
      groupConfigs: [
        {
          types: [NOTIFICATION_TYPE.TELETRANSMISSION],
          title: 'Télétransmissions',
          icon: 'wifi_tethering',
          notifKey: 'teletransmission',
        },
        {
          types: [NOTIFICATION_TYPE.INSTANCE],
          title: 'Instances',
          icon: 'business_center',
          notifKey: 'instance',
        },
        {
          types: [NOTIFICATION_TYPE.GLOBAL],
          title: 'Notifications',
          icon: 'notifications_none',
        },
        {
          types: [NOTIFICATION_TYPE.BIENETRE],
          title: 'Bien-être',
          icon: 'tag_faces',
          notifKey: 'bienetre',
        },
        {
          types: [NOTIFICATION_TYPE.TRANSFERT],
          title: 'Transferts',
          icon: 'reply',
          notifKey: 'transfert',
        },
      ],
    }
  },
  computed: {
    ...mapGetters('notification', [
      'notifications',
      'notificationById',
      'notifications_pending',
    ]),
    ...mapGetters('user', ['user']),
    forfaitJourQuestions() {
      // On isole seulement les questions forfait jour non lues pour forcer la popup.
      return (
        this.notifications
          .filter(
            (notification) =>
              NOTIFICATION_STATE.UNREAD === notification.state &&
              this.isForfaitJourQuestion(notification),
          )
          // On traite d'abord la question la plus récente si plusieurs sont présentes.
          .sort((left, right) => (right.created || 0) - (left.created || 0))
      )
    },
    groups() {
      const notifs = getDeepValue(this.user, 'dashboard.notifications')

      return this.groupConfigs
        .filter((group) => !group.notifKey || notifs?.[group.notifKey])
        .map((group, index) => {
          const notifications = this.notifications.filter(
            (notification) =>
              group.types.includes(notification.type) &&
              !(
                notification.type === NOTIFICATION_TYPE.GLOBAL &&
                this.isForfaitJourQuestion(notification)
              ),
          )

          const unread = notifications.filter(
            (notification) => notification.state === NOTIFICATION_STATE.UNREAD,
          ).length

          return {
            key: `${index}-${group.notifKey}`,
            type: group.type,
            title: group.title,
            icon: group.icon,
            notifications,
            unread,
          }
        })
    },
    groupsNotEmpty() {
      return this.groups.filter((group) => group.notifications.length > 0)
    },
  },
  watch: {
    forfaitJourQuestions: {
      immediate: true,
      handler() {
        this.openForfaitJourQuestion()
      },
    },
  },
  async beforeMount() {
    await this.refreshNotifs()

    this.refreshNotif = setIntervalIfVisible(() => {
      this.refreshNotifs()
    }, 10000)
  },
  beforeDestroy() {
    if (this.refreshNotif) {
      this.refreshNotif()
    }
  },
  methods: {
    isForfaitJourQuestion(notification) {
      return (
        getDeepValue(notification, 'data.question.key') ===
        FORFAIT_JOUR_QUESTION_KEY
      )
    },
    async openForfaitJourQuestion() {
      if (this.forfaitJourQuestionPending) {
        return
      }

      const question = this.forfaitJourQuestions[0]
      if (!question) {
        return
      }

      this.forfaitJourQuestionPending = true

      try {
        // La popup bloque l'interface tant que la réponse n'a pas été donnée.
        const answer = await swal({
          title: question.title || 'Forfait jour',
          text:
            question.description ||
            'Est-ce que cette demie-journée est travaillée ?',
          icon: 'warning',
          buttons: {
            no: {
              text: 'Non',
              value: false,
            },
            yes: {
              text: 'Oui',
              value: true,
            },
          },
          closeOnClickOutside: false,
          closeOnEsc: false,
        })

        await this.$store.dispatch('notification/answer', {
          model: this.notificationById(question.id),
          answer: Boolean(answer),
        })
      } finally {
        // Si plusieurs questions sont déjà en attente, on les traite l'une après l'autre.
        this.forfaitJourQuestionPending = false

        if (this.forfaitJourQuestions.length > 0) {
          this.openForfaitJourQuestion()
        }
      }
    },
    async refreshNotifs() {
      if (this.groups.length > 0) {
        await this.$store.dispatch('notification/refresh')
      }
    },
  },
}
</script>
